import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { McqQuestion } from "./McqQuestion";
import { SECTORS, GEO_ZONES } from "./constants";

/** Step 1 — Company identity (Heetch Pro dark) */
export function StepCompany({ form, up }) {
  const [siretPreview, setSiretPreview] = useState(null);
  const [siretLoading, setSiretLoading] = useState(false);
  const [cityInput, setCityInput] = useState("");

  const lookupSiret = async () => {
    setSiretLoading(true);
    try {
      const { data } = await api.get(`/siret/${form.siret.replace(/\s/g, "")}`);
      setSiretPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SIRET introuvable");
    } finally {
      setSiretLoading(false);
    }
  };

  const applySiret = () => {
    up("company_name", siretPreview.company_name || form.company_name);
    if (siretPreview.city && !form.cities.includes(siretPreview.city)) {
      up("cities", [...form.cities, siretPreview.city]);
    }
    setSiretPreview(null);
    toast.success("Informations confirmées");
  };

  const addCity = () => {
    if (cityInput.trim()) {
      up("cities", [...form.cities, cityInput.trim()]);
      setCityInput("");
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl px-5 py-4 border border-emerald-500/30 bg-emerald-500/10">
        <p className="text-sm text-zinc-300 leading-relaxed">
          Ces informations servent à <strong className="text-emerald-300">identifier votre entreprise</strong> dans la base SIRET — tout est confirmé par vous.
        </p>
      </div>

      {/* Company name */}
      <div className="hp-card p-5">
        <Label className="hp-label" style={{ color: "#86efac" }}>
          Nom de votre entreprise
        </Label>
        <Input
          value={form.company_name}
          onChange={(e) => up("company_name", e.target.value)}
          className="hp-input mt-3 h-12 text-base w-full"
          data-testid="ob-company-name"
          placeholder="Ex : Rénovation Dubois"
        />
      </div>

      {/* SIRET autofill */}
      <div className="hp-card p-5">
        <Label className="hp-label" style={{ color: "#86efac" }}>
          SIRET <span className="text-zinc-500 font-normal normal-case tracking-normal">(Prosperr remplit le reste)</span>
        </Label>
        <div className="flex gap-2 mt-3">
          <Input
            value={form.siret}
            onChange={(e) => up("siret", e.target.value)}
            className="hp-input flex-1 h-12 text-base"
            data-testid="ob-siret"
            placeholder="14 chiffres (ou 9 SIREN)"
          />
          <button
            onClick={lookupSiret}
            disabled={siretLoading || form.siret.replace(/\s/g, "").length < 9}
            data-testid="ob-siret-lookup"
            className="hp-btn hp-btn-secondary shrink-0 h-12 px-5"
          >
            {siretLoading ? "…" : "Auto"}
          </button>
        </div>
        {siretPreview && (
          <div className="mt-4 rounded-lg p-4 border border-emerald-500/40 bg-emerald-500/10" data-testid="siret-preview">
            <p className="hp-label mb-2" style={{ color: "#86efac" }}>
              Prosperr a trouvé — confirmez-vous ?
            </p>
            <p className="font-semibold text-base text-[var(--text)]">{siretPreview.company_name}</p>
            <p className="text-sm text-zinc-400 mt-0.5">
              {[siretPreview.postal_code, siretPreview.city].filter(Boolean).join(" ")}
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={applySiret}
                data-testid="siret-confirm"
                className="hp-btn hp-btn-primary flex-1 h-11"
              >
                Confirmer
              </button>
              <button
                onClick={() => setSiretPreview(null)}
                data-testid="siret-reject"
                className="hp-btn hp-btn-secondary flex-1 h-11"
              >
                Modifier
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sector MCQ */}
      <McqQuestion
        title="Secteur d'activité principal"
        mode="single"
        options={SECTORS}
        value={form.sector}
        onChange={(v) => up("sector", v)}
        testidPrefix="q-sector"
      />

      {/* Zone */}
      <McqQuestion
        title="Zone d'intervention"
        mode="single"
        options={GEO_ZONES}
        value={form.zone}
        onChange={(v) => up("zone", v)}
        testidPrefix="q-zone"
        columns={1}
      />

      {/* Cities */}
      <div className="hp-card p-5">
        <Label className="hp-label" style={{ color: "#86efac" }}>
          Villes ou départements
        </Label>
        <div className="flex gap-2 mt-3">
          <Input
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && cityInput.trim()) {
                e.preventDefault();
                addCity();
              }
            }}
            placeholder="Tapez puis Entrée"
            className="hp-input flex-1 h-12 text-base"
            data-testid="ob-city-input"
          />
          <button
            onClick={addCity}
            className="hp-btn hp-btn-secondary px-6 h-12 text-base"
          >
            +
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {form.cities.map((c, i) => (
            <span
              key={i}
              className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-2"
            >
              {c}
              <button type="button" onClick={() => up("cities", form.cities.filter((x) => x !== c))} className="opacity-70 hover:opacity-100">×</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
